import { useState } from 'react';
import { MultipleSurveyQuestion, Survey, SurveyQuestion } from 'posthog-js';
import Button from 'src/components/primitives/Button/Button';
import {
	parsePostHogSurveyDescription,
	storeHasSeenSurvey,
	validatePostHogSurveyAnswer,
} from 'src/components/UserSurvey/utils';
import { cj } from 'src/utils/classNames';
import InputUi from 'src/components/primitives/Input/InputUi';
import RadioGroup from 'src/components/primitives/RadioGroup/RadioGroup';
import { BaseRadioItemProps } from 'src/components/primitives/RadioGroup/types';
import { UserSurveyContentProps } from 'src/components/UserSurvey/types';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';

type Props = Omit<UserSurveyContentProps, 'closeSurvey'>;

export default function UserSurveyQuestionPage({
	survey,
	currentPage,
	setCurrentPage,
}: Props) {
	const { captureEvent } = usePostHogCapture();

	const [surveySentEventData, setSurveySentEventData] = useState(() =>
		initSurveySentEventData(survey)
	);

	const currentQuestion = survey.questions[currentPage - 1];

	const { id, optional, description, buttonText, type } = currentQuestion ?? {};
	const validation = (
		currentQuestion as SurveyQuestion & {
			validation?: { type: string; value?: number }[];
		}
	)?.validation;

	if (
		// `id` is required for PostHog.
		!id ||
		// Can't do anything if we don't have a question.
		!currentQuestion ||
		// We currently only support these question types from the API.
		(type !== 'open' && type !== 'single_choice')
	) {
		return null;
	}

	const answer = surveySentEventData[getSurveyAnswerKey(id)];

	const updateAnswer = (questionId: string, value: string) => {
		setSurveySentEventData((prev) => ({
			...prev,
			[getSurveyAnswerKey(questionId)]: value,
		}));
	};

	const handleBack = () => {
		setCurrentPage(currentPage - 1);
	};

	const handleNext = () => {
		// If this was the last question, send the survey data to PostHog and store
		// a flag in `localStorage` to prevent the survey from being shown again.
		if (currentPage === numQuestions) {
			captureEvent(
				'survey sent',
				surveySentEventData as unknown as {
					$survey_id: string;
					[key: string]: unknown;
				}
			);

			storeHasSeenSurvey(survey.id);
		}

		setCurrentPage(currentPage + 1);
	};

	const numQuestions = survey.questions.length;

	const isAnswerValid = validatePostHogSurveyAnswer(answer, {
		type,
		optional,
		validation,
	});

	const inputContent = (() => {
		if (type === 'open') {
			const { type: openInputType, placeholder } =
				parsePostHogSurveyDescription(description);
			const isTextArea = openInputType === 'textarea';
			const FormField = isTextArea ? 'textarea' : 'input';

			return (
				<InputUi.Container>
					<InputUi.Value asChild size="md">
						<FormField
							key={id}
							value={answer}
							className={cj(isTextArea && 'h-32 resize-none')}
							onChange={(e) => updateAnswer(id, e.target.value)}
							placeholder={placeholder}
							autoFocus
						/>
					</InputUi.Value>
				</InputUi.Container>
			);
		}

		// The other option is `single_choice`, for which we use `RadioGroup`.
		const items: BaseRadioItemProps<string>[] = (
			currentQuestion as MultipleSurveyQuestion
		).choices.map((choice) => ({ id: choice, value: choice, label: choice }));

		return (
			<RadioGroup.Default
				size="lg"
				variant="card"
				orientation="vertical"
				className="gap-y-lg"
				value={answer}
				onValueChange={(value) => updateAnswer(id, value)}
				items={items}
				key={id}
			/>
		);
	})();

	return (
		<div className="flex flex-col gap-y-lg">
			{inputContent}

			<div className="flex items-center justify-between">
				<div className="typo-label-xs text-text-secondary">
					<span className="text-text-default">{currentPage}</span>/
					{numQuestions}
				</div>

				<div className="flex items-center gap-x-sm">
					{currentPage > 1 && (
						<Button size="md" variant="stroke" onClick={handleBack}>
							Back
						</Button>
					)}

					<Button size="md" onClick={handleNext} disabled={!isAnswerValid}>
						{buttonText ?? (currentPage === numQuestions ? 'Submit' : 'Next')}
					</Button>
				</div>
			</div>
		</div>
	);
}

type SurveyAnswerKey<T extends string> = `$survey_response_${T}`;

interface SurveySentEventData {
	$survey_id: string;
	$survey_questions: SurveyQuestion[];
	[key: SurveyAnswerKey<string>]: string;
}

function initSurveySentEventData(survey: Survey) {
	// This object's keys are defined by PostHog. See the following docs for more details:
	// https://posthog.com/tutorials/nextjs-surveys#4-capture-interactions-from-it
	const data: SurveySentEventData = {
		$survey_id: survey.id,
		$survey_questions: survey.questions,
	};

	for (const question of survey.questions) {
		const { id } = question;

		// Without an `id`, we can't really do anything. For some reason, PostHog
		// types it as `string | undefined` but also requires it to be sent up in
		// the `survey sent` capture event.
		if (!id) {
			console.warn('[UserSurvey] No id found for question', question);
			continue;
		}

		// Initialize the answer to an empty string.
		data[getSurveyAnswerKey(id)] = '';
	}

	return data;
}

function getSurveyAnswerKey<T extends string | undefined>(
	questionId: T
): T extends string ? SurveyAnswerKey<T> : undefined;
function getSurveyAnswerKey(
	questionId: string | undefined
): SurveyAnswerKey<string> | undefined {
	if (questionId === undefined) {
		return;
	}

	return `$survey_response_${questionId}`;
}
